import React, { FC, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  GetDailyStandupSettingsResponse,
  UpdateDailyStandupSettingsResponse,
  DailyStandupSettings,
} from '@dreamai/types';
import api from '../../api';
import './daily-standup-section.scss';

const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const COMMON_TIMEZONES = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'Europe/Helsinki',
  'Europe/Athens',
  'Europe/Bucharest',
  'Europe/Moscow',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'America/Mexico_City',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Pacific/Auckland',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Cairo',
];

const getTimezoneOptions = (): string[] => {
  const set = new Set(COMMON_TIMEZONES);
  set.add(BROWSER_TZ);
  return Array.from(set).sort();
};

const TIMEZONE_OPTIONS = getTimezoneOptions();

const DailyStandupSection: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [settings, setSettings] = useState<DailyStandupSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Local form state
  const [callTime, setCallTime] = useState('07:30');
  const [timezone, setTimezone] = useState(BROWSER_TZ);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<GetDailyStandupSettingsResponse>(
          '/daily-standup/settings',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data.success && res.data.settings) {
          const s = res.data.settings;
          setSettings(s);
          setCallTime(s.callTime.slice(0, 5));
          setTimezone(s.timezone);
        }
      } catch (err) {
        console.error('Error fetching standup settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [getAccessTokenSilently]);

  const hasChanges = useCallback((): boolean => {
    if (!settings) return true;
    return (
      callTime !== settings.callTime.slice(0, 5) ||
      timezone !== settings.timezone
    );
  }, [settings, callTime, timezone]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.put<UpdateDailyStandupSettingsResponse>(
        '/daily-standup/settings',
        {
          enabled: true,
          callTime,
          timezone,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        setSettings(res.data.settings);
        setFeedback({ type: 'success', message: 'Settings saved' });
        setTimeout(() => setFeedback(null), 3000);
      }
    } catch (err) {
      console.error('Error saving standup settings:', err);
      setFeedback({
        type: 'error',
        message: 'Failed to save. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  }, [callTime, timezone, getAccessTokenSilently]);

  if (loading) {
    return (
      <div className='daily-standup-section'>
        <div className='settings-skeleton' />
      </div>
    );
  }

  return (
    <div className='daily-standup-section'>
      <div className='daily-standup-header'>
        <h2 className='section-title'>Daily Rundown</h2>
        <p className='daily-standup-subtitle'>
          Your assistant will call you each morning for a quick sync — a
          briefing on your day, emails, calendar, and a rotating life topic.
        </p>
      </div>

      <div className='daily-standup-card'>
        <div className='daily-standup-field'>
          <label className='field-label' htmlFor='ds-call-time'>
            Call time
          </label>
          <input
            id='ds-call-time'
            type='time'
            className='field-input'
            value={callTime}
            onChange={(e) => setCallTime(e.target.value)}
          />
        </div>

        <div className='daily-standup-field'>
          <label className='field-label' htmlFor='ds-timezone'>
            Timezone
          </label>
          <select
            id='ds-timezone'
            className='field-input'
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz} value={tz}>
                {tz.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
      </div>

      {feedback && (
        <p className={`daily-standup-feedback ${feedback.type}`}>
          {feedback.message}
        </p>
      )}

      <div className='daily-standup-actions'>
        <button
          className='btn-save'
          onClick={handleSave}
          disabled={saving || !hasChanges()}
        >
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

export default DailyStandupSection;
